'use client'

import {
  COOKIE_CONSENT_CHANGED_EVENT,
  COOKIE_CONSENT_STORAGE_KEY,
  getCookieConsentPreferences,
} from '@/lib/cookie-consent'
import Script from 'next/script'
import { useEffect, useRef, useState } from 'react'

const GOOGLE_ID = 'G-KCX65SP5D0'
const REDDIT_ID = 'a2_ed4bbk1dzfmk'

function googleConsent(preferences) {
  return {
    analytics_storage: preferences?.analytics ? 'granted' : 'denied',
    ad_storage: preferences?.advertising ? 'granted' : 'denied',
    ad_user_data: preferences?.advertising ? 'granted' : 'denied',
    ad_personalization: preferences?.advertising ? 'granted' : 'denied',
  }
}

export default function ConsentScripts() {
  const [preferences, setPreferences] = useState(null)
  const googleInitialized = useRef(false)
  const redditInitialized = useRef(false)

  useEffect(() => {
    const sync = () => {
      const next = getCookieConsentPreferences()
      if (next?.advertising && !window.rdt) {
        window.rdt = function (...args) {
          if (window.rdt.sendEvent) window.rdt.sendEvent(...args)
          else window.rdt.callQueue.push(args)
        }
        window.rdt.callQueue = []
      }
      window[`ga-disable-${GOOGLE_ID}`] = !next?.analytics
      if (googleInitialized.current) {
        window.gtag?.('consent', 'update', googleConsent(next))
      }
      if (redditInitialized.current) {
        // Reddit's init configuration supports optOut for an existing pixel.
        window.rdt?.('init', REDDIT_ID, { optOut: !next?.advertising })
      }
      setPreferences(next)
    }
    const onStorage = (event) => {
      if (event.key === COOKIE_CONSENT_STORAGE_KEY || event.key === null) sync()
    }
    sync()
    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, sync)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, sync)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  const initializeGoogle = () => {
    // The preference may change while the external script is downloading.
    const current = getCookieConsentPreferences()
    if (!current?.analytics || googleInitialized.current) return
    window.dataLayer = window.dataLayer || []
    window.gtag =
      window.gtag ||
      function () {
        window.dataLayer.push(arguments)
      }
    window.gtag('consent', 'default', googleConsent(current))
    window.gtag('js', new Date())
    window.gtag('config', GOOGLE_ID, { page_path: window.location.pathname })
    googleInitialized.current = true
  }

  const initializeReddit = () => {
    if (
      !getCookieConsentPreferences()?.advertising ||
      redditInitialized.current
    )
      return
    window.rdt?.('init', REDDIT_ID)
    window.rdt?.('track', 'PageVisit')
    redditInitialized.current = true
  }

  return (
    <>
      {preferences?.analytics && (
        <Script
          id="google-analytics"
          src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ID}`}
          strategy="lazyOnload"
          onReady={initializeGoogle}
        />
      )}
      {preferences?.advertising && (
        <Script
          id="reddit-pixel"
          src="https://www.redditstatic.com/ads/pixel.js"
          strategy="lazyOnload"
          onReady={initializeReddit}
        />
      )}
    </>
  )
}
